import { Button, Icon, Typography } from "@saviynt/design-system";
import { useIntl } from "react-intl";
import { useCertificateExpiry } from "../hooks";
import { formatPemCertificate, downloadCertificate } from "../utils";
import ExpiryBadge from "./ExpiryBadge";

interface CertificateCardProps {
  /** Label for the certificate (e.g., "Signing Certificate") */
  label: string;
  /** Base64-encoded certificate content */
  certBase64: string | null | undefined;
  /** Filename for the downloaded certificate */
  fileName: string;
  /** Whether the card interactions are disabled */
  isDisabled?: boolean;
}

/**
 * Displays a single certificate with download button and expiry badge.
 * Shows a disabled state when the certificate is not available.
 */
export default function CertificateCard({
  label,
  certBase64,
  fileName,
  isDisabled = false,
}: CertificateCardProps) {
  const intl = useIntl();
  const { expiryDate } = useCertificateExpiry(certBase64);

  const isUnavailable = !certBase64 || isDisabled;

  const handleDownload = () => {
    if (!certBase64) return;
    const pemContent = formatPemCertificate(certBase64);
    downloadCertificate(pemContent, fileName);
  };

  return (
    <div className="CertSection__card">
      <div className="CertSection__cardInfo">
        <Typography kind="body2-bold" color="--color-foreground-neutral-bold">
          {label}
        </Typography>
        {isUnavailable ? (
          <Typography kind="body3" color="--color-foreground-neutral-subtle">
            {intl.formatMessage({
              id: "vRP3eY",
              defaultMessage: "Certificate not yet available",
              description:
                "Text shown when certificate is not available for download",
            })}
          </Typography>
        ) : (
          <ExpiryBadge expiryDate={expiryDate} />
        )}
      </div>
      <Button
        kind="outlined"
        size="medium"
        leftIcon={<Icon kind="Download" size="small" />}
        onClick={handleDownload}
        isDisabled={isUnavailable}
        aria-label={intl.formatMessage(
          {
            id: "9P/Km6",
            defaultMessage: "Download {label}",
            description: "Aria label for certificate download button",
          },
          { label },
        )}
      >
        {intl.formatMessage({
          id: "vLSV4h",
          defaultMessage: "Download",
          description: "Certificate download button label",
        })}
      </Button>
    </div>
  );
}
